/**
 * Credit Utilities - Standardized Credit Formatting for Frontend
 * 
 * This module provides utilities for displaying credits with consistent
 * decimal precision across the entire frontend. All credit values from
 * the backend are now returned as floats with 2 decimal precision.
 * 
 * Key principles:
 * - Display up to 2 decimal places when needed
 * - Remove trailing zeros for cleaner display (e.g., "100" instead of "100.00")
 * - Format with locale-aware thousand separators
 */

/**
 * Format a credit value for display.
 * 
 * @param {number|string|null|undefined} value - Credit value to format
 * @param {object} options - Formatting options
 * @param {boolean} options.alwaysShowDecimals - Always show 2 decimal places
 * @param {boolean} options.showSign - Show + prefix for positive values
 * @returns {string} Formatted credit string
 * 
 * @example
 * formatCredits(100)        // "100"
 * formatCredits(25.5)       // "25.5"
 * formatCredits(0.25)       // "0.25"
 * formatCredits(1000)       // "1,000"
 * formatCredits(100, { alwaysShowDecimals: true })  // "100.00"
 * formatCredits(50, { showSign: true })              // "+50"
 */
export function formatCredits(value, options = {}) {
  const { alwaysShowDecimals = false, showSign = false } = options;
  
  // Handle null, undefined, NaN
  if (value === null || value === undefined || isNaN(Number(value))) {
    return alwaysShowDecimals ? '0.00' : '0';
  }
  
  const num = typeof value === 'string' ? parseFloat(value) : value;
  
  // Handle zero
  if (num === 0) {
    return alwaysShowDecimals ? '0.00' : '0';
  }
  
  let formatted;
  
  if (alwaysShowDecimals) {
    // Always show 2 decimal places
    formatted = num.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  } else {
    // Show decimals only if needed (remove trailing zeros)
    const hasDecimals = num % 1 !== 0;
    
    if (hasDecimals) {
      // Check if we need 1 or 2 decimal places
      const oneDecimal = Math.round(num * 10) / 10;
      const twoDecimal = Math.round(num * 100) / 100;
      
      if (oneDecimal === twoDecimal) {
        // One decimal is enough (e.g., 25.5)
        formatted = num.toLocaleString('en-IN', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        });
      } else {
        // Need two decimals (e.g., 25.25)
        formatted = num.toLocaleString('en-IN', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      }
    } else {
      // Integer - no decimals needed
      formatted = num.toLocaleString('en-IN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    }
  }
  
  // Add sign prefix if requested
  if (showSign && num > 0) {
    formatted = '+' + formatted;
  }
  
  return formatted;
}

/**
 * Format credits with a "cr" suffix for compact display.
 * 
 * @param {number|string} value - Credit value
 * @returns {string} Formatted string with "cr" suffix
 * 
 * @example
 * formatCreditsShort(100)   // "100 cr"
 * formatCreditsShort(4.5)   // "4.5 cr"
 */
export function formatCreditsShort(value) {
  return `${formatCredits(value)} cr`;
}

/**
 * Format a credit balance with sign indicator.
 * Positive values show as credits (green), negative as debits (red).
 * 
 * @param {number} value - Credit value
 * @returns {object} { text, isCredit }
 */
export function formatCreditTransaction(value) {
  const num = Number(value) || 0;
  const isCredit = num > 0;
  const text = `${isCredit ? '+' : ''}${formatCredits(num)}`;
  
  return { text, isCredit };
}

/**
 * Parse a credit value from any format to a number.
 * 
 * @param {any} value - Value to parse
 * @returns {number} Parsed number (0 if invalid)
 */
export function parseCredits(value) {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    // Remove commas and parse
    const cleaned = value.replace(/,/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
}

/**
 * Round credits to 2 decimal places.
 * 
 * @param {number} value - Credit value
 * @returns {number} Rounded value
 */
export function roundCredits(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

/**
 * Check if balance is sufficient for an operation.
 * 
 * @param {number} balance - Current balance
 * @param {number} required - Required credits
 * @returns {boolean} True if sufficient
 */
export function hasSufficientCredits(balance, required) {
  return (Number(balance) || 0) >= (Number(required) || 0);
}

/**
 * Format a usage balance value as INR (₹) currency.
 * Used for displaying Usage Balance, Included Balance, and Add Usage amounts.
 *
 * @param {number|string|null|undefined} value - Balance value in INR
 * @param {object} options
 * @param {boolean} options.showSymbol - Prepend ₹ symbol (default: true)
 * @param {boolean} options.showSign - Show + prefix for positive values
 * @returns {string}
 *
 * @example
 * formatBalance(1500)         // "₹1,500"
 * formatBalance(22.5)         // "₹22.50"
 * formatBalance(1500, { showSymbol: false })  // "1,500"
 */
export function formatBalance(value, options = {}) {
  const { showSymbol = true, showSign = false } = options;

  if (value === null || value === undefined || isNaN(Number(value))) {
    return showSymbol ? '₹0' : '0';
  }

  const num = typeof value === 'string' ? parseFloat(value) : Number(value);

  // Determine decimal places: show .50 for halves, none for integers
  const hasDecimals = num % 1 !== 0;
  const formatted = num.toLocaleString('en-IN', {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2
  });

  const prefix = (showSign && num > 0 ? '+' : '') + (showSymbol ? '₹' : '');
  return prefix + formatted;
}

// Named export alias
export const formatUsageBalance = formatBalance;